/**
 * CSV text parser with quoted field support (RFC 4180-style).
 * Pure function, no side effects.
 */

export type ParseResult =
  | { ok: true; headers: string[]; rows: string[][] }
  | { ok: false; message: string };

/**
 * Parse CSV text into headers and data rows.
 * Handles quoted fields containing commas and escaped quotes.
 */
export function parseCsvText(text: string): ParseResult {
  const lines = splitLines(text);
  if (lines.length === 0) {
    return { ok: false, message: "File is empty." };
  }

  const parsedRows: string[][] = [];
  for (const line of lines) {
    const row = parseCsvLine(line);
    if (row === null) {
      return { ok: false, message: "Invalid CSV: unclosed quoted field." };
    }
    parsedRows.push(row);
  }

  const headers = parsedRows[0];
  if (!headers || headers.length === 0) {
    return { ok: false, message: "CSV has no header row." };
  }

  const rows = parsedRows.slice(1);
  const columnCount = headers.length;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].length !== columnCount) {
      return {
        ok: false,
        message: `Row ${i + 2} has ${rows[i].length} columns; expected ${columnCount}.`,
      };
    }
  }

  return { ok: true, headers, rows };
}

/** Split text into lines, normalizing line endings. */
function splitLines(text: string): string[] {
  return text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .map((s) => s.trimEnd())
    .filter((s, i, arr) => {
      const isLast = i === arr.length - 1;
      return s.length > 0 || !isLast;
    });
}

/**
 * Parse a single CSV line into fields.
 * Returns null if a quoted field is unclosed.
 */
export function parseCsvLine(line: string): string[] | null {
  const fields: string[] = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '"') {
      const { value, next } = parseQuotedField(line, i);
      if (next === -1) return null;
      fields.push(value);
      i = next;
    } else {
      const { value, next } = parseUnquotedField(line, i);
      fields.push(value);
      i = next;
    }
  }
  // Handle trailing comma (empty field at end of line)
  if (line.length > 0 && line[line.length - 1] === ",") {
    fields.push("");
  }
  return fields;
}

function parseQuotedField(
  line: string,
  start: number,
): { value: string; next: number } {
  let out = "";
  let i = start + 1;
  while (i < line.length) {
    const c = line[i];
    if (c === '"') {
      // Check for escaped quote ("")
      if (i + 1 < line.length && line[i + 1] === '"') {
        out += '"';
        i += 2;
        continue;
      }
      // Found closing quote
      i++;
      // Skip optional whitespace after closing quote
      while (i < line.length && line[i] === " ") i++;
      // Skip the comma delimiter if present (but only ONE comma)
      if (i < line.length && line[i] === ",") i++;
      return { value: out, next: i };
    }
    out += c;
    i++;
  }
  // Reached end of line without closing quote
  return { value: "", next: -1 };
}

function parseUnquotedField(
  line: string,
  start: number,
): { value: string; next: number } {
  let end = start;
  while (end < line.length && line[end] !== ",") end++;
  const value = line.slice(start, end).trim();
  const next = end < line.length ? end + 1 : end;
  return { value, next };
}
